import React, { useRef, createRef, useLayoutEffect, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from "react-router-dom";
import { getNavbarHeight, getViewportDimensions } from '../../../models/Utils';
import { getBBFIndex } from '../../../models/Utils';
import {
  setHasVisitedBBF
} from '../../../store/reducers/mainReducer';

import VideoBanner from '../subcomponents/VideoBanner/VideoBanner';
import CreditsWrapper from '../subcomponents/CreditsWrapper/CreditsWrapper';
import MosaicSection from '../subcomponents/MosaicSection/MosaicSection';
import ExhibitMenu from '../../Common/ExhibitMenu/ExhibitMenu';

import { ADALandmarks } from '../../../models/ADA';
import './PillarPage.scss';

const snappingTolerance = 10;
const mobileScreenThreshold = 767; // MUST BE IN SYNC WITH CSS

const PillarPage = () => {
  const dispatch = useDispatch();
  const creditsElement = useRef(null);
  const videoBannerRef = useRef(null);
  const mosaicSections = useRef([]);
  const isAutoScrolling = useRef(false);
  const blockMouseWheel = useRef(false);
  const lastScrollPos = useRef(0);
  const isMobileScreen = useRef(false);

  const initialized = useSelector(state => state.data.initialized);
  const digitalMuseumCMS = useSelector(state => state.data.cms.digitalMuseum);
  const poweredByPurposeCMS = useSelector(state => state.data.cms.poweredByPurpose);
  const cmsData = useSelector(state => state.data.cms.buildingBrighterFutures);
  const globalData = useSelector(state => state.data.cms.global);
  const navbar = useSelector(state => state.data.menus.buildingBrighterFutures);
  const cmsMediaPath = useSelector(state => state.config.basePath + state.config.apiMediaPath);
  const hasVisitedStories = useSelector(state => state.data.hasVisitedStories);
  const creditsOpened = useSelector(state => state.data.creditsOpened);
  const menuOpened = useSelector(state => state.data.menuOpened);
  const [index, setIndex] = useState(-1);
  const { pillarId } = useParams();
  const history = useHistory();

  const pillarIndex = getBBFIndex(pillarId, cmsData, history);
  const thePillar = cmsData && cmsData.Pillars ? cmsData.Pillars[pillarIndex] : null;

  useEffect(() => {
    dispatch(setHasVisitedBBF(true));

  }, []);

  const scrollAutomaticallyTo = (y) => {
    isAutoScrolling.current = true;
    blockMouseWheel.current = true;
    const start = document.body.scrollTop;
    const end = y;
    const speed = 500;
    const t0 = Date.now();
    const tFinal = t0 + (Math.abs(end - start) / 1000) * speed;
    const animate = () => {
      let t = Date.now();
      if (t >= tFinal) t = tFinal;
      let tNormalized = (t - t0) / (tFinal - t0);
      const distDiff = (end - start) * (--tNormalized * tNormalized * tNormalized + 1); // easeOutCubic
      document.body.scrollTo(0, start + distDiff);
      if (t < tFinal) {
        requestAnimationFrame(animate);
      } else {
        setTimeout(() => {
          isAutoScrolling.current = false;
        }, 100);
        setTimeout(() => {
          blockMouseWheel.current = false;
        }, 1000);
      }
    };
    requestAnimationFrame(animate);
  };

  const scrollToMosaicSection = (index) => {
    setIndex(index);
    scrollAutomaticallyTo(mosaicSections.current[index].current.getBoundingClientRect().y + document.body.scrollTop - getNavbarHeight());
  };

  const scrollToVideoBanner = () => {
    setIndex(-1);
    scrollAutomaticallyTo(0);
  };

  const scrollToFooter = () => {
    if(creditsElement && creditsElement.current){
      const bottomRect = creditsElement.current.getBoundingClientRect();
      const viewport = getViewportDimensions();
      scrollAutomaticallyTo(document.body.scrollTop + bottomRect.y + bottomRect.height - viewport.height);
    }
  };

  const handleResize = () => {
    isMobileScreen.current = getViewportDimensions().width <= mobileScreenThreshold;
    document.body.scrollTo(0, 0);
  };

  const handleMouseWheel = (e) => {
    if (isMobileScreen.current) return;

    e.preventDefault();
    e.stopPropagation();

    handleDefaultEvent(e);
  }

  const handleKeyDown = (e) => {
    if (isMobileScreen.current) return;

    handleDefaultEvent(e);
  }

  const handleDefaultEvent = (e) => {
    const threshold = 2;
    if (!isAutoScrolling.current && !blockMouseWheel.current && Math.abs(e.deltaY) > threshold) {
      const direction = e.deltaY > 0 ? 'down' : 'up';
      let refY = getNavbarHeight();
      const viewport = getViewportDimensions();
      const bannerRect = videoBannerRef.current.getBoundingClientRect();
      const sectionRects = mosaicSections.current.map((section) => section.current.getBoundingClientRect());
      const bottomRect = creditsElement.current.getBoundingClientRect();
      if (refY > -1) {
        if (refY - snappingTolerance < bannerRect.y && refY + snappingTolerance > bannerRect.y) {
          if (direction === 'down') {
            scrollToMosaicSection(0);
          }
        } else if (viewport.height - snappingTolerance < bottomRect.y + bottomRect.height && viewport.height + snappingTolerance > bottomRect.y + bottomRect.height) {
          if (direction === 'up') {
            scrollToMosaicSection(sectionRects.length - 1);
          }
        }
        else {
          let snappedToSection = false;
          for (let i = 0; i < sectionRects.length; i++) {
            if (refY - snappingTolerance < sectionRects[i].y && refY + snappingTolerance > sectionRects[i].y) {
              if (direction === 'down') {
                if (i < sectionRects.length - 1) {
                  scrollToMosaicSection(i + 1);
                } else {
                  scrollToFooter();
                }
                snappedToSection = true;
                break;
              } else if (direction === 'up') {
                if (i > 0) {
                  scrollToMosaicSection(i - 1);
                } else {
                  scrollToVideoBanner();
                }
                snappedToSection = true;
                break;
              }
            }
          }
          if (!snappedToSection) {
            if (direction === 'down') {
              scrollToFooter();
            } else if (direction === 'up') {
              scrollToVideoBanner();
            }
          }
        }
      }
    }
  };

  useLayoutEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useLayoutEffect(() => {
    // modern Chrome requires { passive: false } when adding event
    let supportsPassive = false;
    try {
      window.addEventListener(
        'test',
        null,
        Object.defineProperty({}, 'passive', {
          get: function () {
            supportsPassive = true;
          },
        })
      );
    } catch (e) {}
    const wheelOpt = supportsPassive ? { passive: false } : false;
    const wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';

    function AddEvents(){
      document.body.addEventListener(wheelEvent, handleMouseWheel, wheelOpt);
      document.body.addEventListener('keydown', handleKeyDown, false);
    }

    function RemoveEvents(){
      document.body.removeEventListener(wheelEvent, handleMouseWheel, wheelOpt);
      document.body.removeEventListener('keydown', handleKeyDown, false);
    }

    if(!creditsOpened && !menuOpened){
      console.log('Add overhaul scroll');
      AddEvents();
    } else {
      console.log('Remove overhaul scroll');
      RemoveEvents();
    }

    return () => {
      RemoveEvents();
    };
  }, [creditsOpened, menuOpened]);

  if(!initialized || !thePillar || pillarIndex < 0){
    return null;
  }

  thePillar.Themes.forEach(theme => {
    theme.MosaicColumns.forEach(column => {
      if (column.Column.BigImage.Image && column.Column.BigImage.Image.indexOf(cmsMediaPath) < 0) {
        column.Column.BigImage.Image = cmsMediaPath + column.Column.BigImage.Image;
      }
      if (column.Column.RowImage.Image && column.Column.RowImage.Image.indexOf(cmsMediaPath) < 0) {
        column.Column.RowImage.Image = cmsMediaPath + column.Column.RowImage.Image;
      }
      if (column.HalfColumn.Image1.Image && column.HalfColumn.Image1.Image.indexOf(cmsMediaPath) < 0) {
        column.HalfColumn.Image1.Image = cmsMediaPath + column.HalfColumn.Image1.Image;
      }
      if (column.HalfColumn.Image2.Image && column.HalfColumn.Image2.Image.indexOf(cmsMediaPath) < 0) {
        column.HalfColumn.Image2.Image = cmsMediaPath + column.HalfColumn.Image2.Image;
      }
    });
  });

  const pillarImages = cmsData.Introduction.ExploreMore.Cards.map(card => {
    return (card.Background.Image && card.Background.Image.indexOf(cmsMediaPath) < 0) 
      ? cmsMediaPath + card.Background.Image
      : card.Background.Image;
  });
  
  const nextPillarIndex = (cmsData && cmsData.Pillars && pillarIndex > -1 && pillarIndex + 1 < cmsData.Pillars.length) ? pillarIndex + 1 : null;
  const nextPillarId = nextPillarIndex ? cmsData.Pillars[nextPillarIndex] : null;

  const getMosaicSections = (pillar) => {
    mosaicSections.current = pillar.Themes.map((theme) => createRef());
    return pillar.Themes.map((theme, i) => {
      return (
        <MosaicSection
          key={`mosaicSection-${i}`}
          ref={mosaicSections.current[i]}
          header={theme.Header}
          body={theme.Body}
          columns={theme.MosaicColumns}
          ctaData={theme.WhereAreWeNow}
          isActive={i===index}
        />
      );
    });
  };

  return (
    <div className="bbf-pillar-page" role={ADALandmarks.Main} >
      <ExhibitMenu title={cmsData ? cmsData.Introduction.Title : ""} links={navbar}/>
      <VideoBanner
        ref={videoBannerRef}
        bgndVideo={thePillar.InitialVideo.Background}
        title={thePillar.InitialVideo.Header}
        text={thePillar.InitialVideo.Body}
        onGoDown={ () => scrollToMosaicSection(0) }
      />
      {getMosaicSections(thePillar)}
      <div className="credits-el" ref={creditsElement}>
      <CreditsWrapper 
        next={
          nextPillarId ? `/${cmsData.Path.Text}/${nextPillarId.Menu.Path}`:
          !hasVisitedStories ? 
            `/${digitalMuseumCMS.Path.Text}/` : 
            `/${poweredByPurposeCMS.Path.Text}/${poweredByPurposeCMS.Introduction.Menu.Path}`
        }
        title={
          nextPillarId ? `Up next: ${cmsData.Pillars[nextPillarIndex].Name}` :
          !hasVisitedStories ? 
            globalData.BottomNavbar.UpNext.Stories.Title : 
            globalData.BottomNavbar.UpNext.PBP.Title
        }
        subtitle={
          nextPillarId ? cmsData.Pillars[nextPillarIndex].InitialVideo.Header :
          !hasVisitedStories ? 
            globalData.BottomNavbar.UpNext.Stories.Subtitle : 
            globalData.BottomNavbar.UpNext.PBP.Subtitle
        }
          />
      </div>
    </div>
  );
};

export default PillarPage;
